import { Grid } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DocumentType } from "generated-graphql/graphql";
import { forwardRef, useCallback, useImperativeHandle } from "react";

import type { useManifestReviewSection } from "../hooks/useManifestReviewSection";
import type { TabSectionHandle } from "../types";

import { ManifestDetailSection } from "./ManifestDetailSection";

type ManifestTabSectionProps = {
  manifest: ReturnType<typeof useManifestReviewSection>;
  documentId: string | undefined;
  documentTenantId: string | null | undefined;
  documentType: DocumentType;
  canShowManifestReview: boolean;
  tenantId: string;
};

export const ManifestTabSection = forwardRef<TabSectionHandle, ManifestTabSectionProps>(
  function ManifestTabSection(
    { manifest, documentId, documentTenantId, documentType, canShowManifestReview, tenantId },
    ref
  ) {
    const alerts = useAlerts();

    const save = useCallback(async () => {
      if (manifest.manifestDataLoaded) {
        await manifest.saveManifest();
        return true;
      } else if (
        documentType === DocumentType.RcraManifest &&
        canShowManifestReview &&
        !manifest.hasManifestData &&
        documentId &&
        documentTenantId
      ) {
        // Scenario B: user classified as manifest but no manifest data exists yet
        await manifest.queueExtractionIfNeeded(documentId, documentTenantId);
        alerts.info(
          "Manifest extraction queued. You can review the extracted manifest data from the Waste Manifests page once processing completes."
        );
        return true;
      }
      return false;
    }, [manifest, documentType, canShowManifestReview, documentId, documentTenantId, alerts]);

    useImperativeHandle(
      ref,
      () => ({
        save,
        isDirty: manifest.manifestIsDirty,
      }),
      [save, manifest.manifestIsDirty]
    );

    return (
      <Grid size={{ xs: 12 }}>
        <ManifestDetailSection manifest={manifest} documentId={documentId} tenantId={tenantId} />
      </Grid>
    );
  }
);
